import { purchaseOfferByClubCoin } from '@wwe-portal/web/services/payment/api';
import { NotSufficientClubCoins } from '@wwe-portal/web/utils/club-store/exception';
import { useMutation } from '@tanstack/react-query';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import { useTranslations } from 'next-intl';
import { useInvalidateExclusiveItems } from '../../providers/queries/club-store';
import { useProcessingDialog } from '@wwe-portal/web/hooks/payment/processing-dialog';

import {
  useGetClubCoins,
  useInvalidateBalance,
} from '@wwe-portal/web/providers/queries/currency';
import { useHandleCommonError } from '@wwe-portal/web/utils/error-handler/error';
import { NotEnoughClubCoinDialog } from '@wwe-portal/web/components/club-store/modals/NotEnoughClubCoinDialog';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import { AxiosResponse } from 'axios';
import { useClubcoinTransactionAnalytics } from '@wwe-portal/web/libs/analytics/club-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { checkOfferValiditiy } from '@wwe-portal/web/services/client/offer';
import { useInvalidateOffer } from '../payment/use-invalidate-offer';

interface UsePurchaseByClubCoinProps {
  onError?: () => void;
  onPaymentSuccess?: (
    data: AxiosResponse<any>,
    variables: { offerData: OfferData }
  ) => void;
}
const usePurchaseByClubCoins = ({
  onError,
  onPaymentSuccess,
}: UsePurchaseByClubCoinProps) => {
  const handleCommonError = useHandleCommonError();
  const t = useTranslations();
  const invalidateExclusiveItems = useInvalidateExclusiveItems();
  const invalidateOffer = useInvalidateOffer();
  const invalidateBalance = useInvalidateBalance();
  const { amount } = useGetClubCoins();
  const { sendPurchaseSuccessEvent } = useClubcoinTransactionAnalytics();
  const currentUser = useGetCurrentUser();

  const { showProcess, showSuccess } = useProcessingDialog({
    progressData: {
      duration: VERIFY_DURATION_MS,
      title: t('clubCoin_title_load'),
      description: t('clubCoin_description_load'),
    },
    successData: {
      title: t('clubCoin_title_successful'),
      description: t('clubCoin_description_successful'),
    },
    onClose: () => {
      reset();
    },
  });

  const handleOnError = (error: any) => {
    if (NotSufficientClubCoins.NotSufficientClubCoins(error)) {
      showDialog(NotEnoughClubCoinDialog);
    } else {
      handleCommonError(error);
    }
    onError?.();
    reset();
  };

  const isNotEnoughClubCoins = (offerData: OfferData) => {
    return (
      amount == null ||
      (offerData.AlternativeCostAmount != null &&
        amount < offerData.AlternativeCostAmount)
    );
  };

  const verifyAfterPurchase = (purchaseResult: any) => {
    const paymentInfo = purchaseResult.data;
    if (
      NotSufficientClubCoins.NotSufficientClubCoins(purchaseResult) ||
      !paymentInfo.data.success
    ) {
      throw new Error(paymentInfo.data.message);
    }
  };
  const handleOnSuccess = (
    purchaseResult: AxiosResponse<any>,
    params: { offerData: OfferData }
  ) => {
    if (!purchaseResult.data.data.success) {
      throw Error(purchaseResult.data.data.message);
    }
    verifyAfterPurchase(purchaseResult);
    invalidateBalance();
    invalidateExclusiveItems();
    invalidateOffer();
    showProcess();
    setTimeout(() => {
      onPaymentSuccess?.(purchaseResult, params);
      sendPurchaseSuccessEvent(params.offerData, {
        currentUser,
        balance: { clubCoin: amount },
      });

      showSuccess();
    }, VERIFY_DURATION_MS);
  };

  const {
    mutate: purchaseItemsByClubCoins,
    isPending,
    variables,
    reset,
  } = useMutation({
    mutationFn: async ({ offerData }: { offerData: OfferData }) => {
      if (isNotEnoughClubCoins(offerData)) {
        throw new NotSufficientClubCoins();
      }
      const validationResult = await checkOfferValiditiy(offerData.OffersID);
      if (!validationResult.data.data.success) {
        throw new Error(validationResult.data.data.message);
      }
      return purchaseOfferByClubCoin({ offerId: offerData.OffersID });
    },

    onSuccess: handleOnSuccess,
    onError: handleOnError,
  });

  return {
    purchasingOffer: variables,
    purchaseItemsByClubCoins,
    isPending,
  };
};

export default usePurchaseByClubCoins;
